import { EnumSchema, Executor, FromSchema, OptionalString } from '@40seas/entities/common';
import {
    Address,
    AddressSchema,
    BaseEntity,
    BusinessType,
    Currency,
    Identification,
    IdentificationSchema,
    PaymentOptionWithFee,
    PaymentOptionWithFeeSchema,
    WithBaseEntitySchema,
} from '@40seas/models';
import { z } from 'zod';
import { BusinessContact, BusinessContactSchema } from './business-contact.entity';
import { BusinessCreditLine, BusinessCreditLineSchema } from './business.creditline.entity';
import { BusinessFinancialConnection, BusinessFinancialConnectionSchema } from './business.financial-connection.entity';
import { BusinessIdentityVerification, BusinessIdentityVerificationSchema } from './business.identity.verification.entity';
import { BusinessInsurance, BusinessInsuranceSchema } from './business-insurance.entity';
import { BusinessCreditReview, BusinessCreditReviewSchema } from './business.credit-review.entity';

export const BusinessSchema = WithBaseEntitySchema(
    z.object({
        types: z.array(EnumSchema(BusinessType)),
        externalId: z.string().nullish(),
        displayName: z.string().min(2).max(250).nullish(),
        legalName: z.string().min(2).max(250).nullish().openapi({ description: 'Required for identity matching to work.' }),
        slug: z.string().min(2).max(100).nullish(),
        website: z.string().nullish(),
        email: OptionalString(z.string().email()),
        phone: z.string().max(70).nullish(),
        defaultPaymentMethod: z.string().nullish(),
        autoConfirmPaymentMethod: z.string().nullish(),
        dba: z.array(z.string().min(1)).nullish(),
        address: AddressSchema.nullish().openapi({
            description:
                'Business address. For identity matching to work, address.country is required (ISO country code). Other address fields (state, city, addressLine1, addressLine2, zipCode) are optional but improve matching results.',
        }),
        identification: z.array(IdentificationSchema).nullish(),
        contacts: z.array(BusinessContactSchema).nullish(),
        currency: EnumSchema(Currency).nullish(),
        paymentOptions: z.array(PaymentOptionWithFeeSchema).nullish(),
        logo: z.string().min(1).nullish(),
        creditLine: BusinessCreditLineSchema.nullish(),
        insurance: BusinessInsuranceSchema.nullish(),
        identityVerification: BusinessIdentityVerificationSchema.nullish(),
        financialConnections: z.array(BusinessFinancialConnectionSchema).nullish(),
        skipFinancialConnection: z.boolean().optional().nullish(),
        hasAccount: z.boolean().nullish(),
        reference: z.string().nullish(),
        creditReview: BusinessCreditReviewSchema.optional().nullish(),
        vaName: z.string().min(2).max(250).nullish(),
    }),
).strict();

export class Business extends FromSchema(BusinessSchema) implements BaseEntity {
    id: string;
    types: BusinessType[];
    externalId?: string;
    displayName: string;
    legalName: string;
    slug: string;
    website?: string;
    email?: string;
    phone?: string;
    dba?: string[];
    address: Address;
    identification?: Identification[];
    contacts?: BusinessContact[];
    currency?: Currency;
    paymentOptions?: PaymentOptionWithFee[];
    seller?: string | Business;
    logo?: string;
    defaultPaymentMethod?: string;
    autoConfirmPaymentMethod?: string;
    createdAt: Date;
    updatedAt: Date;
    tenantId: string;
    createdBy: Executor;
    creditLine?: BusinessCreditLine;
    insurance?: BusinessInsurance;
    financialConnections?: BusinessFinancialConnection[];
    skipFinancialConnection?: boolean;
    identityVerification?: BusinessIdentityVerification;
    hasAccount?: boolean;
    reference?: string;
    creditReview?: BusinessCreditReview;
    vaName?: string;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}
